/**
 * AI Recruit - Standalone Apply Matching Page
 * Global view of all matching results with filters. Also lets users trigger new matches.
 */

import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  UserGroupIcon,
  BriefcaseIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

import { matchingApi, jobsApi, usersApi } from '@/services/api'
import { toDisplayPercent } from '@/components/talentos'
import { SCORE_RANGES } from '@/utils/matchFilters'
import type { ScoreRange } from '@/utils/matchFilters'
import { getHighestDegreeAndField } from '@/utils/educationSummary'
import { useCandidateStages } from '@/hooks/useCandidateStages'
import SearchableUserDropdown from '@/components/matching/SearchableUserDropdown'
import StatusFilterDropdown from '@/components/matching/StatusFilterDropdown'
import ScoreRangeFilter from '@/components/matching/ScoreRangeFilter'
import ApplicationStatusDropdown from '@/components/matching/ApplicationStatusDropdown'
import MatchingStatusBadge from '@/components/matching/MatchingStatusBadge'
import {
  AssignmentBadge,
  ViewAuditTrailButton,
  AssignmentEditPopover,
  AuditTrailModal,
} from '@/components/assignment'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { usePermission } from '@/hooks/usePermission'


// ─── Score range → API params ──────────────────────────────────────────────
function scoreRangeToParams(range: ScoreRange): { min_score?: number; max_score?: number } {
  if (range === 'all') return {}
  const r = SCORE_RANGES[range]
  return { min_score: r.min, max_score: r.max }
}

const ApplyMatchingPage: React.FC = () => {
  // Added 2026-08-24 (frontend/backend gate mismatch fix) — this page had
  // zero can() checks at all; the "Assigned to" reassignment control and
  // the "View Audit Trail" button both rendered for every authenticated
  // user regardless of role, relying entirely on the backend to reject
  // unauthorized attempts (assignments.manageJobMatches /
  // assignments.viewAuditHistory are both real, already-enforced
  // capabilities — see JobDetailPage.tsx/ResumeDetailPage.tsx for the
  // manageJobMatches precedent this mirrors). Kept as two independent
  // checks, not one shared flag: unlike Job/Resume Detail (where the
  // audit-trail button is gated on manageJobMatches too — a known,
  // already-logged bug, BUG_FIX_LOG.md #17 — viewAuditHistory is wired
  // to its own real key here, not copied from that bug.
  const { can } = usePermission()
  const canEditAssignments = can('assignments.manageJobMatches')
  const canViewAuditHistory = can('assignments.viewAuditHistory')
  // Can change a candidate's status (this page's status dropdown)
  const canChangeCandidateStatus = can('candidateStatus.change')
  // Added 2026-08-25 (permission-catalog round 2, §26) — triggering a new
  // AI match run had no gate at all. Default-open to all roles, matching
  // today's actual unrestricted behavior exactly — flagged separately for
  // a business review since, unlike a passive view, this consumes real
  // AI/compute spend.
  const canNewMatch = can('matching.newMatch')

  // ─── "New Match" section (collapsible) ────────────────────────────────────
  const [newMatchOpen, setNewMatchOpen] = useState(false)

  // Candidate selection (for triggering new match) — multi-select, enabled only after a job is chosen
  const [candidateSearch, setCandidateSearch] = useState('')
  const [candidateQuery, setCandidateQuery] = useState('')
  const [selectedCandidates, setSelectedCandidates] = useState<any[]>([])

  // Job selection (for triggering new match) — single select
  const [jobSearch, setJobSearch] = useState('')
  const [jobQuery, setJobQuery] = useState('')
  const [selectedJob, setSelectedJob] = useState<any>(null)

  // Matching trigger
  const [comparisonLoading, setComparisonLoading] = useState(false)

  // ─── Global results filters ───────────────────────────────────────────────
  const [filtersExpanded, setFiltersExpanded] = useState(true)

  // Job filter
  const [jobFilterSearch, setJobFilterSearch] = useState('')
  const [jobFilterQuery, setJobFilterQuery] = useState('')
  const [jobFilterId, setJobFilterId] = useState<string | null>(null)
  const [jobFilterLabel, setJobFilterLabel] = useState('')
  const [showJobFilterResults, setShowJobFilterResults] = useState(false)

  // Candidate filter
  const [candidateFilterSearch, setCandidateFilterSearch] = useState('')
  const [candidateFilterQuery, setCandidateFilterQuery] = useState('')
  const [candidateFilterId, setCandidateFilterId] = useState<string | null>(null)
  const [candidateFilterLabel, setCandidateFilterLabel] = useState('')
  const [showCandidateFilterResults, setShowCandidateFilterResults] = useState(false)

  // Assigned To, Status, Score Range filters
  const [assignedUserId, setAssignedUserId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [scoreRange, setScoreRange] = useState<ScoreRange>('all')

  // Pagination for global results
  const [globalPage, setGlobalPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Application status optimistic updates
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, string>>({})

  // Audit trail
  const [auditModal, setAuditModal] = useState<{
    isOpen: boolean
    entityId: string
    entityTitle: string
  }>({ isOpen: false, entityId: '', entityTitle: '' })

  // Assignment edit
  const [assignmentEdit, setAssignmentEdit] = useState<{
    isOpen: boolean
    entityId: string
    entityTitle: string
    currentUserId: string | null
    currentUserName: string | null
  }>({ isOpen: false, entityId: '', entityTitle: '', currentUserId: null, currentUserName: null })

  // ─── Active filter count ──────────────────────────────────────────────────
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (jobFilterId) count++
    if (candidateFilterId) count++
    if (assignedUserId) count++
    if (statusFilter) count++
    if (scoreRange !== 'all') count++
    return count
  }, [jobFilterId, candidateFilterId, assignedUserId, statusFilter, scoreRange])

  // ─── Queries: new-match search panels ────────────────────────────────────
  const { data: newMatchCandidatesData, isLoading: newMatchCandidatesLoading } = useQuery({
    queryKey: ['nm-candidates', candidateQuery],
    queryFn: () => matchingApi.searchResumes({ query: candidateQuery, page_size: 50 }),
    enabled: !!candidateQuery && newMatchOpen,
    staleTime: 60000,
    refetchOnMount: 'always',
  })
  const newMatchCandidates = newMatchCandidatesData?.data?.resumes || []

  const { data: newMatchJobsData, isLoading: newMatchJobsLoading } = useQuery({
    queryKey: ['nm-jobs', jobQuery],
    queryFn: () => jobsApi.search({ query: jobQuery, page_size: 50 }),
    enabled: !!jobQuery && newMatchOpen,
    staleTime: 60000,
    refetchOnMount: 'always',
  })
  const newMatchJobs = newMatchJobsData?.data?.jobs || []

  // ─── Queries: existing matches for the selected job/candidate ───────────────
  // When a job is selected → fetch its existing matched resume IDs
  // → hide those candidates from the candidate search results
  const { data: jobExistingMatchData } = useQuery({
    queryKey: ['existing-matches-for-job', selectedJob?.id],
    queryFn: () =>
      matchingApi.getAllMatchingResults({ job_id: selectedJob.id, page_size: 500 }),
    enabled: !!selectedJob && newMatchOpen,
    staleTime: 30 * 1000,
  })

  // resume_ids already matched with selectedJob → filter from candidate results
  const matchedResumeIds = useMemo(() => {
    const ids = new Set<string>()
    ;(jobExistingMatchData?.data?.comparison_results || []).forEach((m: any) =>
      ids.add(m.resume_id)
    )
    return ids
  }, [jobExistingMatchData])

  // Filtered candidate results — only show candidates not yet matched to the selected job
  const filteredNewMatchCandidates = newMatchCandidates.filter(
    (c: any) => !matchedResumeIds.has(c.id)
  )

  // ─── Queries: filter dropdowns ────────────────────────────────────────────
  const { data: filterJobsData, isLoading: filterJobsLoading } = useQuery({
    queryKey: ['filter-jobs', jobFilterQuery],
    queryFn: () => jobsApi.search({ query: jobFilterQuery, page_size: 30 }),
    enabled: !!jobFilterQuery,
    staleTime: 60000,
    refetchOnMount: 'always',
  })
  const filterJobs = filterJobsData?.data?.jobs || []

  const { data: filterCandidatesData, isLoading: filterCandidatesLoading } = useQuery({
    queryKey: ['filter-candidates', candidateFilterQuery],
    queryFn: () => matchingApi.searchResumes({ query: candidateFilterQuery, page_size: 30 }),
    enabled: !!candidateFilterQuery,
    staleTime: 60000,
    refetchOnMount: 'always',
  })
  const filterCandidates = filterCandidatesData?.data?.resumes || []

  // Org users for Assigned To dropdown
  const { data: usersData } = useQuery({
    queryKey: ['org-users'],
    queryFn: () => usersApi.list({ page_size: 100, is_active: true }),
    staleTime: 5 * 60 * 1000,
  })
  const orgUsers = useMemo(
    () =>
      (usersData?.data?.users || []).map((u: any) => ({
        id: String(u.id),
        name: u.full_name || u.email || 'Unknown',
      })),
    [usersData]
  )

  // ─── Query: global results ────────────────────────────────────────────────
  const scoreParams = scoreRangeToParams(scoreRange)
  const { data: globalData, isLoading: globalLoading, refetch: refetchGlobal } = useQuery({
    queryKey: [
      'global-matching-results',
      jobFilterId,
      candidateFilterId,
      assignedUserId,
      statusFilter,
      scoreRange,
      globalPage,
      pageSize,
    ],
    queryFn: () =>
      matchingApi.getAllMatchingResults({
        job_id: jobFilterId ?? undefined,
        resume_id: candidateFilterId ?? undefined,
        matched_by_id: assignedUserId ?? undefined,
        candidate_status_id: statusFilter ?? undefined,
        ...scoreParams,
        page: globalPage,
        page_size: pageSize,
      }),
  })

  const globalResults: any[] = globalData?.data?.comparison_results || []
  const globalPagination = globalData?.data?.pagination || { total: 0, total_pages: 1, page: 1 }
  const globalSummary = globalData?.data?.summary_insights || null

  // Candidate statuses loaded dynamically from the backend for the filter dropdown
  const { flatStatuses } = useCandidateStages()
  const uniqueStatuses = flatStatuses.map((s) => ({
    value: s.id,
    label: s.statusName,
  }))

  // ─── Handlers: clear all filters ─────────────────────────────────────────
  const handleClearFilters = () => {
    setJobFilterId(null)
    setJobFilterLabel('')
    setJobFilterSearch('')
    setJobFilterQuery('')
    setCandidateFilterId(null)
    setCandidateFilterLabel('')
    setCandidateFilterSearch('')
    setCandidateFilterQuery('')
    setAssignedUserId(null)
    setStatusFilter(null)
    setScoreRange('all')
    setGlobalPage(1)
  }

  // ─── Handlers: new match trigger ──────────────────────────────────────────
  const handleApplyMatching = async () => {
    if (!selectedJob || selectedCandidates.length === 0) {
      toast.error('Please select a job and at least one candidate')
      return
    }
    try {
      setComparisonLoading(true)
      const response = await matchingApi.compareCandidatesAsync(
        [selectedJob.id],
        selectedCandidates.map((c: any) => c.id)
      )
      toast.success(response.data?.message || 'Matching queued successfully')
      setSelectedCandidates([])
      setSelectedJob(null)
      setCandidateSearch('')
      setCandidateQuery('')
      setJobSearch('')
      setJobQuery('')
      setNewMatchOpen(false)
      setGlobalPage(1)
      setTimeout(() => {
        refetchGlobal()
        setComparisonLoading(false)
      }, 2500)
    } catch {
      toast.error('Failed to start matching. Please try again.')
      setComparisonLoading(false)
    }
  }

  const handleApplicationStatusChange = async (
    jobId: string,
    resumeId: string,
    newStatus: string,
    comment: string
  ) => {
    // Optimistic update — show the change immediately in the UI
    setApplicationStatuses((prev) => ({ ...prev, [resumeId]: newStatus }))
    try {
      await matchingApi.updateApplicationStatus(jobId, resumeId, newStatus, comment)
    } catch {
      toast.error('Failed to update status. Please try again.')
      // Revert optimistic update by re-fetching current state
      refetchGlobal()
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const toggleCandidate = (resume: any) => {
    setSelectedCandidates((prev) =>
      prev.some((c) => c.id === resume.id)
        ? prev.filter((c) => c.id !== resume.id)
        : [...prev, resume]
    )
  }

  // ─── Render: candidate card (new match selection) ─────────────────────────
  const renderNewMatchCandidateCard = (resume: any) => {
    const isSelected = selectedCandidates.some((c) => c.id === resume.id)
    const isBlacklisted = resume.blacklist
    return (
      <div
        key={resume.id}
        onClick={() => !isBlacklisted && toggleCandidate(resume)}
        className={`p-3 border rounded-lg transition-all cursor-pointer ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : isBlacklisted
            ? 'border-red-200 bg-red-50 opacity-60 cursor-not-allowed'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center space-x-3">
          {/* Checkbox indicator */}
          <div
            className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
              isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
            }`}
          >
            {isSelected && (
              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-1.5">
              {resume.candidate_id && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-mono">
                  {resume.candidate_id}
                </span>
              )}
              <span className="font-medium text-sm text-gray-900">{resume.candidate_name}</span>
              {isBlacklisted && (
                <span className="text-xs text-red-600 flex items-center">
                  <ExclamationTriangleIcon className="w-3 h-3 mr-0.5" />
                  Blacklisted
                </span>
              )}
            </div>
            {resume.professional_title && (
              <div className="text-xs text-gray-500 mt-0.5">{resume.professional_title}</div>
            )}
          </div>
          {resume.total_years_experience != null && (
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {resume.total_years_experience} yrs
            </span>
          )}
        </div>
      </div>
    )
  }

  const renderNewMatchJobCard = (job: any) => {
    const isSelected = selectedJob?.id === job.id
    return (
      <div
        key={job.id}
        onClick={() => {
          if (isSelected) {
            setSelectedJob(null)
          } else {
            setSelectedJob(job)
            setSelectedCandidates([])
          }
        }}
        className={`p-3 border rounded-lg transition-all cursor-pointer ${
          isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center space-x-3">
          {/* Radio indicator */}
          <div
            className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
              isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
            }`}
          >
            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-1.5">
              {job.job_id && (
                <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-mono">
                  {job.job_id}
                </span>
              )}
              <span className="font-medium text-sm text-gray-900">{job.title}</span>
            </div>
            {job.department && <div className="text-xs text-gray-500 mt-0.5">{job.department}</div>}
          </div>
        </div>
      </div>
    )
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <ArrowsRightLeftIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Apply Matching</h1>
            <p className="text-sm text-gray-500">
              Global view of all AI-powered match analyses across all jobs and candidates
            </p>
          </div>
        </div>
        {canNewMatch && (
          <button
            onClick={() => setNewMatchOpen((v) => !v)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusCircleIcon className="w-4 h-4" />
            <span>New Match</span>
            {newMatchOpen ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* ── New Match collapsible section ─────────────────────────────────── */}
      {newMatchOpen && canNewMatch && (
        <div className="bg-white shadow-sm rounded-lg border border-blue-200 p-6 space-y-5">
          <h2 className="text-base font-semibold text-gray-900">Trigger New Match</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Job panel */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <BriefcaseIcon className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-gray-700">Select Job</span>
              </div>
              <div className="flex space-x-2">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && setJobQuery(jobSearch.trim())}
                    placeholder="Job ID or role name..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => setJobQuery(jobSearch.trim())}
                  disabled={!jobSearch.trim()}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  Search
                </button>
                {jobQuery && (
                  <button
                    onClick={() => {
                      setJobSearch('')
                      setJobQuery('')
                      setSelectedJob(null)
                      setSelectedCandidates([])
                    }}
                    className="px-2 py-1.5 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              {jobQuery && (
                <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                  {newMatchJobsLoading ? (
                    <div className="flex justify-center py-4">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : newMatchJobs.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-4">No jobs found</p>
                  ) : (
                    newMatchJobs.map(renderNewMatchJobCard)
                  )}
                </div>
              )}
              {selectedJob && !jobQuery && (
                <div className="flex items-center justify-between p-2.5 bg-purple-50 rounded-lg border border-purple-200 text-sm">
                  <span className="font-medium text-purple-800">{selectedJob.title}</span>
                  <button
                    onClick={() => {
                      setSelectedJob(null)
                      setSelectedCandidates([])
                    }}
                  >
                    <XMarkIcon className="w-4 h-4 text-purple-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Candidate panel — disabled until a job is selected */}
            <div className={`space-y-3 ${!selectedJob ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="flex items-center space-x-2">
                <UserGroupIcon className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-700">
                  Select Candidate{selectedCandidates.length > 0 ? ` (${selectedCandidates.length} selected)` : ''}
                </span>
                {!selectedJob && (
                  <span className="text-xs text-gray-400 italic">— select a job first</span>
                )}
              </div>
              <div className="flex space-x-2">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && setCandidateQuery(candidateSearch.trim())}
                    placeholder="Name, ID, title, or skills..."
                    disabled={!selectedJob}
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <button
                  onClick={() => setCandidateQuery(candidateSearch.trim())}
                  disabled={!candidateSearch.trim() || !selectedJob}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Search
                </button>
                {candidateQuery && (
                  <button
                    onClick={() => {
                      setCandidateSearch('')
                      setCandidateQuery('')
                    }}
                    className="px-2 py-1.5 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              {candidateQuery && (
                <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                  {newMatchCandidatesLoading ? (
                    <div className="flex justify-center py-4">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : filteredNewMatchCandidates.length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-4">
                      {newMatchCandidates.length > 0
                        ? 'All matching candidates are already matched with this job'
                        : 'No candidates found'}
                    </p>
                  ) : (
                    filteredNewMatchCandidates.map(renderNewMatchCandidateCard)
                  )}
                </div>
              )}
              {/* Selected candidate chips */}
              {selectedCandidates.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedCandidates.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-800"
                    >
                      {c.candidate_name}
                      <button
                        onClick={() => setSelectedCandidates((prev) => prev.filter((x) => x.id !== c.id))}
                        className="text-blue-400 hover:text-blue-600"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Apply Matching bar */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
              <span className={selectedJob ? 'text-purple-700 font-medium' : 'text-gray-400 italic'}>
                {selectedJob ? `💼 ${selectedJob.title}` : 'No job selected'}
              </span>
              <ArrowsRightLeftIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
              <span className={selectedCandidates.length > 0 ? 'text-blue-700 font-medium' : 'text-gray-400 italic'}>
                {selectedCandidates.length > 0
                  ? `👤 ${selectedCandidates.length} candidate${selectedCandidates.length > 1 ? 's' : ''} selected`
                  : 'No candidates selected'}
              </span>
            </div>
            <button
              onClick={handleApplyMatching}
              disabled={selectedCandidates.length === 0 || !selectedJob || comparisonLoading}
              className="flex items-center space-x-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {comparisonLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <ArrowsRightLeftIcon className="w-4 h-4" />
                  <span>Apply Matching</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Global Results Section ─────────────────────────────────────────── */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {/* Section header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-base font-semibold text-gray-900">All Matching Results</h2>
            {globalSummary?.total_matches != null && (
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {globalSummary.total_matches} total
              </span>
            )}
          </div>
          {globalSummary?.total_matches != null && globalSummary.total_matches > 0 && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Show:</span>
                <select
                  className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px]"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setGlobalPage(1)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setGlobalPage((p) => Math.max(1, p - 1))}
                  disabled={globalPage === 1}
                  className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
                  title="Previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700 px-2">
                  Page {globalPage} of {globalPagination.total_pages}
                </span>
                <button
                  onClick={() => setGlobalPage((p) => Math.min(globalPagination.total_pages, p + 1))}
                  disabled={globalPage >= globalPagination.total_pages}
                  className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
                  title="Next page"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {/* ── Filter Panel ─────────────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Filter header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200">
              <button
                onClick={() => setFiltersExpanded((v) => !v)}
                className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                {filtersExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => refetchGlobal()}
                  className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                  title="Reload results with current filters"
                >
                  <ArrowPathIcon className="w-3.5 h-3.5" />
                  <span>Refresh</span>
                </button>
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Filter content */}
            {filtersExpanded && (
              <div className="px-4 py-4 space-y-4">
                {/* Row 1: Job filter + Candidate filter */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Job filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Job</label>
                    {jobFilterId ? (
                      <div className="flex items-center justify-between px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg text-sm">
                        <div className="flex items-center space-x-2 min-w-0">
                          <BriefcaseIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-blue-800 font-medium truncate">{jobFilterLabel}</span>
                        </div>
                        <button
                          onClick={() => {
                            setJobFilterId(null)
                            setJobFilterLabel('')
                            setJobFilterSearch('')
                            setJobFilterQuery('')
                            setShowJobFilterResults(false)
                            setGlobalPage(1)
                          }}
                          className="ml-2 text-blue-400 hover:text-blue-600 flex-shrink-0"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="flex space-x-2">
                          <div className="flex-1 relative">
                            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                              type="text"
                              value={jobFilterSearch}
                              onChange={(e) => {
                                setJobFilterSearch(e.target.value)
                                setShowJobFilterResults(true)
                                if (!e.target.value.trim()) {
                                  setJobFilterQuery('')
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && jobFilterSearch.trim()) {
                                  setJobFilterQuery(jobFilterSearch.trim())
                                  setShowJobFilterResults(true)
                                }
                              }}
                              placeholder="Search by ID or role..."
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <button
                            onClick={() => {
                              if (jobFilterSearch.trim()) {
                                setJobFilterQuery(jobFilterSearch.trim())
                                setShowJobFilterResults(true)
                              }
                            }}
                            disabled={!jobFilterSearch.trim()}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              jobFilterSearch.trim()
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            Search
                          </button>
                        </div>
                        {showJobFilterResults && jobFilterQuery && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filterJobsLoading ? (
                              <div className="flex justify-center py-4">
                                <LoadingSpinner size="sm" />
                              </div>
                            ) : filterJobs.length === 0 ? (
                              <p className="text-xs text-gray-500 text-center py-3">No jobs found</p>
                            ) : (
                              filterJobs.map((job: any) => (
                                <button
                                  key={job.id}
                                  onClick={() => {
                                    setJobFilterId(job.id)
                                    setJobFilterLabel(job.title)
                                    setJobFilterSearch('')
                                    setJobFilterQuery('')
                                    setShowJobFilterResults(false)
                                    setGlobalPage(1)
                                  }}
                                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                                >
                                  <div className="flex items-center space-x-2">
                                    {job.job_id && (
                                      <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-mono">
                                        {job.job_id}
                                      </span>
                                    )}
                                    <span className="text-sm font-medium text-gray-900">{job.title}</span>
                                  </div>
                                  {job.department && (
                                    <div className="text-xs text-gray-500 mt-0.5 ml-0">{job.department}</div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Candidate filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Candidate
                    </label>
                    {candidateFilterId ? (
                      <div className="flex items-center justify-between px-3 py-2 border border-blue-300 bg-blue-50 rounded-lg text-sm">
                        <div className="flex items-center space-x-2 min-w-0">
                          <UserIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-blue-800 font-medium truncate">{candidateFilterLabel}</span>
                        </div>
                        <button
                          onClick={() => {
                            setCandidateFilterId(null)
                            setCandidateFilterLabel('')
                            setCandidateFilterSearch('')
                            setCandidateFilterQuery('')
                            setShowCandidateFilterResults(false)
                            setGlobalPage(1)
                          }}
                          className="ml-2 text-blue-400 hover:text-blue-600 flex-shrink-0"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="flex space-x-2">
                          <div className="flex-1 relative">
                            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                              type="text"
                              value={candidateFilterSearch}
                              onChange={(e) => {
                                setCandidateFilterSearch(e.target.value)
                                setShowCandidateFilterResults(true)
                                if (!e.target.value.trim()) {
                                  setCandidateFilterQuery('')
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && candidateFilterSearch.trim()) {
                                  setCandidateFilterQuery(candidateFilterSearch.trim())
                                  setShowCandidateFilterResults(true)
                                }
                              }}
                              placeholder="Name, ID, title, or skills..."
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <button
                            onClick={() => {
                              if (candidateFilterSearch.trim()) {
                                setCandidateFilterQuery(candidateFilterSearch.trim())
                                setShowCandidateFilterResults(true)
                              }
                            }}
                            disabled={!candidateFilterSearch.trim()}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              candidateFilterSearch.trim()
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            Search
                          </button>
                        </div>
                        {showCandidateFilterResults && candidateFilterQuery && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filterCandidatesLoading ? (
                              <div className="flex justify-center py-4">
                                <LoadingSpinner size="sm" />
                              </div>
                            ) : filterCandidates.length === 0 ? (
                              <p className="text-xs text-gray-500 text-center py-3">No candidates found</p>
                            ) : (
                              filterCandidates.map((resume: any) => (
                                <button
                                  key={resume.id}
                                  onClick={() => {
                                    setCandidateFilterId(resume.id)
                                    setCandidateFilterLabel(resume.candidate_name)
                                    setCandidateFilterSearch('')
                                    setCandidateFilterQuery('')
                                    setShowCandidateFilterResults(false)
                                    setGlobalPage(1)
                                  }}
                                  className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                                >
                                  <div className="flex items-center space-x-2">
                                    {resume.candidate_id && (
                                      <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-mono">
                                        {resume.candidate_id}
                                      </span>
                                    )}
                                    <span className="text-sm font-medium text-gray-900">
                                      {resume.candidate_name}
                                    </span>
                                  </div>
                                  {resume.professional_title && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {resume.professional_title}
                                    </div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Assigned To + Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Assigned To
                    </label>
                    <SearchableUserDropdown
                      users={orgUsers}
                      selectedUserId={assignedUserId}
                      onChange={(userId) => {
                        setAssignedUserId(userId)
                        setGlobalPage(1)
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                    <StatusFilterDropdown
                      selectedStatus={statusFilter}
                      onChange={(status) => {
                        setStatusFilter(status)
                        setGlobalPage(1)
                      }}
                      availableStatuses={uniqueStatuses}
                    />
                  </div>
                </div>

                {/* Row 3: Score Range */}
                <div>
                  <ScoreRangeFilter
                    selectedRange={scoreRange}
                    onChange={(range) => {
                      setScoreRange(range)
                      setGlobalPage(1)
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Result count ──────────────────────────────────────────────── */}
          {!globalLoading && (
            <div className="text-sm text-gray-500">
              {globalSummary?.total_matches != null ? (
                <>
                  Showing{' '}
                  <span className="font-medium text-gray-700">
                    {(globalPage - 1) * pageSize + 1}–
                    {Math.min(globalPage * pageSize, globalSummary.total_matches)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-gray-700">{globalSummary.total_matches}</span>{' '}
                  results
                  {activeFilterCount > 0 && (
                    <span className="ml-1 text-blue-600">(filtered)</span>
                  )}
                </>
              ) : (
                'No results'
              )}
            </div>
          )}

          {/* ── Match cards ───────────────────────────────────────────────── */}
          <div className="space-y-4">
            {globalLoading ? (
              <div className="flex justify-center py-14">
                <LoadingSpinner size="lg" />
              </div>
            ) : globalResults.length === 0 ? (
              <div className="py-14 text-center">
                <ChartBarIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No matching results found</p>
                {activeFilterCount > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Try clearing some filters or{' '}
                    <button
                      onClick={handleClearFilters}
                      className="text-blue-500 hover:underline"
                    >
                      clear all
                    </button>
                  </p>
                )}
              </div>
            ) : (
              globalResults.map((result: any, index: number) => (
                <div
                  key={result?.id || index}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow bg-white"
                >
                  {/* Row 1: Candidate name + ID badge + score + status */}
                  <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
                    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                      <UserIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      <span className="text-base font-semibold text-gray-900">
                        {result.candidate_name}
                      </span>
                      {result.candidate_id && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-mono">
                          {result.candidate_id}
                        </span>
                      )}
                      {result.id && canViewAuditHistory && (
                        <ViewAuditTrailButton
                          onClick={() =>
                            setAuditModal({
                              isOpen: true,
                              entityId: result.id,
                              entityTitle: result.candidate_name,
                            })
                          }
                        />
                      )}
                      {result.blacklist && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                          Blacklisted
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* gate_status/detailed_analysis come from the same
                          TalentOS-derived job_matches row match_score does
                          (see match_projection_pure.py) -- an 'ineligible'
                          candidate was never scored, so showing "0%" here
                          would read as "scored, and scored terribly" rather
                          than "below the experience minimum". */}
                      {result?.detailed_analysis?.gate_status === 'ineligible' ? (
                        <span className="text-lg font-semibold text-gray-400">Not Eligible</span>
                      ) : (
                        <span className="text-2xl font-bold text-blue-600">
                          {toDisplayPercent(result?.match_score ?? 0)}%
                        </span>
                      )}
                      {/* Hidden (not disabled) without candidateStatus.change,
                          bug fix 2026-08-27 — only this control is gated; the
                          surrounding row and the results list/query itself are
                          untouched. */}
                      {result.job_id && canChangeCandidateStatus && (
                        <ApplicationStatusDropdown
                          currentStatus={
                            applicationStatuses[result.resume_id] ||
                            result.candidate_status_id ||
                            null
                          }
                          oldStatus={result.application_status}
                          matchCreatedAt={result.match_created_at}
                          jobId={result.job_id}
                          resumeId={result.resume_id}
                          onStatusChange={(newStatus, comment) =>
                            handleApplicationStatusChange(result.job_id, result.resume_id, newStatus, comment)
                          }
                        />
                      )}
                    </div>
                  </div>

                  {/* Row 2: Job name + ID badge + department */}
                  {result.job_title && (
                    <div className="flex items-center space-x-2 mb-2 flex-wrap gap-y-1">
                      <BriefcaseIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 font-medium">{result.job_title}</span>
                      {result.job_display_id && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-mono">
                          {result.job_display_id}
                        </span>
                      )}
                      {result.job_department && (
                        <span className="text-xs text-gray-400">· {result.job_department}</span>
                      )}
                    </div>
                  )}

                  {/* Row 3: Candidate professional title */}
                  {result.professional_title && (
                    <div className="text-sm text-gray-500 mb-2">{result.professional_title}</div>
                  )}

                  {/* Row 4: Assigned to */}
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm text-gray-500">Assigned to:</span>
                    <AssignmentBadge
                      userName={result.matched_by_name}
                      assignmentType="manual"
                      clickable={canEditAssignments}
                      onClick={canEditAssignments ? () =>
                        setAssignmentEdit({
                          isOpen: true,
                          entityId: result.id,
                          entityTitle: result.candidate_name,
                          currentUserId: result.matched_by_id,
                          currentUserName: result.matched_by_name,
                        }) : undefined
                      }
                    />
                  </div>

                  {/* Row 5: Matching status badge */}
                  <div className="mb-3">
                    <MatchingStatusBadge
                      status={
                        result.status === 'completed'
                          ? 'completed'
                          : result.status === 'processing'
                          ? 'processing'
                          : result.status === 'pending'
                          ? 'pending'
                          : result.status === 'failed'
                          ? 'failed'
                          : 'unknown'
                      }
                      retryCount={result.retry_count}
                    />
                  </div>

                  {/* Row 6: Skills / Experience / Education */}
                  <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                    <div className="flex items-center space-x-1.5">
                      <span className="text-green-600">✅</span>
                      <span>
                        <span className="font-medium">Skills:</span>{' '}
                        {result?.detailed_analysis?.skills_breakdown?.skills_matched_count ?? 0}/
                        {result?.detailed_analysis?.skills_breakdown?.total_required_skills ?? 0}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <span className="text-orange-500">⭐</span>
                      <span>
                        <span className="font-medium">Experience:</span>{' '}
                        {result?.total_years_experience ?? 0} yrs
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <span className="text-purple-600">🎓</span>
                      <span>
                        <span className="font-medium">Education:</span>{' '}
                        {result?.detailed_analysis?.education_breakdown?.degree_match
                          ? 'Degree Match'
                          : getHighestDegreeAndField(result?.detailed_analysis?.education_summary) || 'Not evaluated'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Pagination ────────────────────────────────────────────────── */}
          {globalSummary?.total_matches != null && globalSummary.total_matches > 0 && (
            <div className="flex justify-center items-center space-x-4 bg-white p-4 rounded-lg shadow">
              <button
                onClick={() => setGlobalPage((p) => Math.max(1, p - 1))}
                disabled={globalPage === 1}
                className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span>Previous</span>
              </button>
              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, globalPagination.total_pages) }, (_, i) => {
                  let startPage = Math.max(1, globalPage - 2)
                  const endPage = Math.min(globalPagination.total_pages, startPage + 4)
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4)
                  }
                  const pageNum = startPage + i
                  if (pageNum > globalPagination.total_pages) return null
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setGlobalPage(pageNum)}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                        globalPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
              <button
                onClick={() => setGlobalPage((p) => Math.min(globalPagination.total_pages, p + 1))}
                disabled={globalPage >= globalPagination.total_pages}
                className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span>Next</span>
                <ChevronRightIcon className="w-4 h-4" />
              </button>
              <div className="text-sm text-gray-600 ml-4">
                Showing {(globalPage - 1) * pageSize + 1} to{' '}
                {Math.min(globalPage * pageSize, globalSummary.total_matches)} of{' '}
                {globalSummary.total_matches} results
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audit Trail Modal */}
      <AuditTrailModal
        isOpen={auditModal.isOpen}
        onClose={() => setAuditModal((prev) => ({ ...prev, isOpen: false }))}
        entityType="job_match"
        entityId={auditModal.entityId}
        entityTitle={auditModal.entityTitle}
      />

      {/* Assignment Edit Popover */}
      <AssignmentEditPopover
        isOpen={assignmentEdit.isOpen}
        onClose={() => setAssignmentEdit((prev) => ({ ...prev, isOpen: false }))}
        entityType="job_match"
        entityId={assignmentEdit.entityId}
        currentUserId={assignmentEdit.currentUserId}
        currentUserName={assignmentEdit.currentUserName}
        onSave={() => {
          setAssignmentEdit((prev) => ({ ...prev, isOpen: false }))
          refetchGlobal()
        }}
      />
    </div>
  )
}

export default ApplyMatchingPage
